import { useState } from 'react'
import { Building2, FileSpreadsheet, Layers } from 'lucide-react'
import { NoPermissionState, useLocale } from '@hk/ui'
import { may } from '@hk/core'
import { RoomLoad } from './RoomLoad'
import { RoomEditor } from './RoomEditor'
import { AreaSetup } from './AreaSetup'
import { STRINGS } from '../i18n/strings'

/* ============================================================================
   W-127 · ROOMS AND AREAS — the room master. (Inventory: W-132.)

   THE CODE WAS WRONG FOR A SPRINT AND IS CORRECTED HERE. This was built as W-135, which
   the approved pack draws as **Go-live data** — a readiness checklist of what must be
   loaded before the first shift, in dependency order, with expected-against-loaded counts.
   That is a different screen and it is NOT built. What is built is the pack's W-127, the
   room master. Looking the code up before naming a screen is the standing rule in
   SCREENS.md; it was not followed, and this is the second numbering defect in the project.

   TWO DELIBERATE DIVERGENCES FROM THE PACK, both for the client to accept or refuse —
   Change Note 03:

   1. **THE PACK DRAWS ROOM NUMBER, TYPE AND FLOOR AS READ-ONLY**, because rooms come from
      the property system and only the CLEANING rules are ours. There is no property system
      in this phase — the client settled on 9 September that staging has no external API and
      no data integration — so a read-only room master would be a screen showing an empty
      table with no way to fill it. They are editable here, and the day a PMS arrives the
      right change is to lock them again, not to keep two ways in.
   2. **THE PACK'S OTHER HALF IS BUILT** — the checklist, the clean time, the deep-clean
      frequency and the cleaning notes PER ROOM, "the boundary the whole product runs on"
      in the pack's own words, including its rule that a room with no cleaning rules is
      never given to anyone. It is a panel on `RoomEditor.tsx`: one room or a selection,
      with "apply the type's defaults" filling only what is empty, and the save reporting
      how many rooms can now be assigned and could not before.

      **THIS COMMENT SAID "NOT BUILT" FOR EIGHT SPRINTS AFTER IT WAS BUILT, AND ON
      18 SEPTEMBER IT PUT A FALSE SENTENCE IN A DOCUMENT SENT TO THE CLIENT.** Change
      Note 03 was written from this comment rather than from the code it sits above;
      SCREENS.md had been corrected in Sprint 53 and this had not. The note was reissued at
      Rev 02 the same day.

      The lesson is the one SCREENS.md already states and this file did not follow: **the
      code is authoritative about what exists.** A comment that describes a sibling file is
      a copy of a fact, and a copy of a fact goes stale silently — which is the same defect
      as #109, #151 and #162, in prose rather than in data.

      The pack also puts room TYPES and PUBLIC AREAS on this screen as further tabs; public
      areas are W-30 in this build, and room types are edited on `RoomEditor.tsx` (Sprint
      49) as well as arriving through the spreadsheet.

   ── what this screen is ──────────────────────────────────────────────────────

   TWO TABS RATHER THAN TWO SIDEBAR ENTRIES, because they are the same job at two
   scales: a spreadsheet is how five hundred rooms arrive before go-live, and the form
   is how one room changes every week afterwards. Two entries in the sidebar would make
   an administrator choose between them before knowing which they wanted — and worse,
   would let the loader be found by somebody who only meant to renumber 305, which is
   the one screen in the product it is easiest to do damage with.

   THE SPREADSHEET IS THE SECOND TAB, not the first. It is the one used once. After the
   first week, every visit to this screen is about one room.
   ========================================================================= */

export function RoomSetup({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const [tab, setTab] = useState<'edit' | 'load' | 'areas'>('edit')

  /* CHECKED ONCE, HERE, rather than in each half. Both are the same permission, and a
     permission checked in two places is a permission that gets changed in one. */
  if (!may('admin.rooms')) return <NoPermissionState />

  return (
    <div className="flex flex-col gap-3.5">
      <div role="tablist" className="flex gap-1.5">
        <Tab
          on={tab === 'edit'}
          onClick={() => setTab('edit')}
          icon={<Building2 className="size-3.5" />}
          label={t.editTab}
        />
        <Tab
          on={tab === 'load'}
          onClick={() => setTab('load')}
          icon={<FileSpreadsheet className="size-3.5" />}
          label={t.loadTab}
        />
        {/* **THE THIRD TAB IS WHERE THE PACK PUT IT** — W-127 is "Rooms and areas", and
            the pack draws public areas as a further tab on it rather than a screen of
            its own. Same permission, already checked above: a corridor on the third
            floor is the same kind of fact about the same building as the rooms along it.

            Added Sprint 88, five sprints after the go-live checklist started asking for
            these. Nothing in this product could create one until then. */}
        <Tab
          on={tab === 'areas'}
          onClick={() => setTab('areas')}
          icon={<Layers className="size-3.5" />}
          label={t.areaTab}
        />
      </div>

      {/* UNMOUNTED, NOT HIDDEN. The loader holds a chosen file and a plan computed from
          it; leaving that alive behind a hidden tab means somebody can come back an hour
          later to a "load 500 rooms" button standing over a plan that was true when they
          left the room. */}
      {tab === 'edit' ? <RoomEditor propertyId={propertyId} />
        : tab === 'load' ? <RoomLoad propertyId={propertyId} />
        : <AreaSetup propertyId={propertyId} />}
    </div>
  )
}

function Tab({
  on, onClick, icon, label,
}: { on: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={on}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-hk border px-3 py-1.5 text-xs font-medium
                  cursor-pointer
                  ${on
                    ? 'border-[var(--hk-primary)] bg-[var(--hk-primary)] text-white'
                    : 'border-border bg-card text-muted-foreground hover:bg-muted'}`}
    >
      {icon}
      {label}
    </button>
  )
}
